import { Box, Button, Checkbox, Flex, Spinner, Text } from '@radix-ui/themes'
import React from 'react'

import { useTranslation } from '@disco/i18n/use-translation'

import styles from './index.module.css'

type Props<T> = {
  query: string
  results: T[]
  isSearching: boolean
  hasError: boolean
  onRetry: () => void
  selectedKeys: (string | number)[]
  isFull: boolean
  getKey: (item: T) => string | number
  getItemLabel: (item: T) => string
  renderItem: (item: T) => React.ReactNode
  onToggle: (item: T) => void
  emptyQueryMessage?: React.ReactNode
}

/**
 * Search results of a `SearchSelectDialog`, each with a checkbox, or the loading,
 * empty-query, error or no-results state.
 * @param query - Current search query
 * @param results - Results for the query
 * @param isSearching - Whether a search is in progress
 * @param hasError - Whether the last search failed
 * @param onRetry - Called to run the search again after an error
 * @param selectedKeys - Keys of the selected items
 * @param isFull - Whether the maximum number of items is selected
 * @param getKey - Unique key for an item
 * @param getItemLabel - Plain-text name of an item, used for accessible labels
 * @param renderItem - Renders a search result
 * @param onToggle - Called with an item when its checkbox is toggled
 * @param emptyQueryMessage - Shown before anything is typed
 */
export const SearchResults = <T,>({
  query,
  results,
  isSearching,
  hasError,
  onRetry,
  selectedKeys,
  isFull,
  getKey,
  getItemLabel,
  renderItem,
  onToggle,
  emptyQueryMessage,
}: Props<T>): React.ReactNode => {
  const { t } = useTranslation('common')

  if (isSearching) {
    return (
      <Flex justify="center" p="4">
        <Spinner />
      </Flex>
    )
  }
  if (query.trim() === '') {
    return (
      emptyQueryMessage && (
        <Text as="p" size="2" color="gray" align="center" my="4">
          {emptyQueryMessage}
        </Text>
      )
    )
  }
  if (hasError) {
    return (
      <Flex direction="column" align="center" gap="3" my="4">
        <Text size="2" color="gray">
          {t(
            'common:couldNotLoadSearchResults',
            'Couldn’t load search results.',
          )}
        </Text>
        <Button variant="soft" onClick={onRetry}>
          {t('common:tryAgain', 'Try again')}
        </Button>
      </Flex>
    )
  }
  if (results.length === 0) {
    return (
      <Text as="p" size="2" color="gray" align="center" my="4">
        {t('common:noResultsFound', 'No results found')}
      </Text>
    )
  }

  return results.map(item => {
    const isSelected = selectedKeys.includes(getKey(item))
    const isDisabled = !isSelected && isFull
    return (
      <Text
        as="label"
        key={getKey(item)}
        className={styles.resultRow}
        data-disabled={isDisabled || undefined}
      >
        <Flex align="center" gap="3" py="2">
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            onCheckedChange={() => onToggle(item)}
            aria-label={getItemLabel(item)}
          />
          <Box flexGrow="1" minWidth="0">
            {renderItem(item)}
          </Box>
        </Flex>
      </Text>
    )
  })
}
